/**
 * Sites page (/strony) — AI-driven website builder.
 *
 * Layout:
 *   ┌─ Header ─────────────────────────────────────────────────────────┐
 *   ├─ Sites list (compact, top) ──────────────────────────────────────┤
 *   ├─ Chat (left, 1/3) ─┬─ Preview (center, big, mobile/desktop) ─────┤
 *   │                    ├─ Sidepanel (right): assets · versions · domains ─
 *   └────────────────────┴──────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest, uploadFile } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import Sidebar from "@/components/sidebar";
import {
  Globe,
  Plus,
  Trash2,
  Send,
  Upload,
  ImagePlus,
  Check,
  AlertCircle,
  RefreshCw,
  Smartphone,
  Monitor,
  Sparkles,
  Copy,
  ExternalLink,
  Loader2,
  Code2,
  Save,
  Maximize2,
  Minimize2,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────

interface HostedSite {
  id: string;
  name: string;
  status: string;
  currentVersionId: string | null;
  createdAt: string;
}

interface HostedVersion {
  id: string;
  versionNumber: number;
  sizeBytes: number;
  fileCount: number;
  uploadedAt: string;
}

interface HostedDomain {
  id: string;
  domain: string;
  status: "pending" | "verified" | "active" | "failed";
  verificationToken: string;
  lastError: string | null;
}

interface SiteDetail {
  site: HostedSite;
  versions: HostedVersion[];
  domains: HostedDomain[];
}

interface BuilderModel {
  key: string;
  label: string;
  modelId: string;
  multiplier: number;
  description: string | null;
}

interface SiteAsset {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  publicUrl: string;
  createdAt: string;
}

interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  /** Set on AI turns when a new draft was saved. */
  savedVersion?: { id: string; versionNumber: number; isPublished: boolean };
  /** Asset URLs attached by the user with this turn (paste / upload). */
  attachedImages?: string[];
}

interface ChatResponse {
  assistantText: string;
  updatedHtml: string | null;
  savedVersion: { id: string; versionNumber: number; isPublished: boolean } | null;
  modelKey: string;
  modelLabel: string;
  tokensRemaining: number;
  tokensCharged: number;
}

interface CurrentHtmlResponse {
  html: string;
  versionId: string | null;
  versionNumber: number | null;
  isPublished?: boolean;
}

// Fallback list shown before /api/sites/builder/models resolves. Source of truth
// is the backend (server/modules/site-hosting/builder-models.ts).
const FALLBACK_MODELS: BuilderModel[] = [
  {
    key: "sonnet-4-6",
    label: "Claude Sonnet 4.6 · Fast",
    modelId: "anthropic/claude-sonnet-4.6",
    multiplier: 1,
    description: null,
  },
  {
    key: "opus-4-7",
    label: "Claude Opus 4.7 · Pro",
    modelId: "anthropic/claude-opus-4.7",
    multiplier: 5,
    description: null,
  },
];

const STATUS_VARIANTS: Record<string, "default" | "destructive" | "secondary" | "outline"> = {
  active: "default",
  verified: "default",
  pending: "secondary",
  failed: "destructive",
  suspended: "destructive",
  deleted: "destructive",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Page ────────────────────────────────────────────────────────────────

export default function SitesPage(): JSX.Element {
  const { t } = useTranslation("sites");
  const { toast } = useToast();
  const qc = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [newSiteName, setNewSiteName] = useState("");
  const [focused, setFocused] = useState(false);

  const sitesQuery = useQuery<HostedSite[]>({
    queryKey: ["/api/sites"],
    queryFn: () => apiRequest("GET", "/api/sites").then((r) => r.data ?? r),
  });

  const createSite = useMutation({
    mutationFn: (name: string) => apiRequest("POST", "/api/sites", { name }),
    onSuccess: (resp: { data?: HostedSite }) => {
      setNewSiteName("");
      qc.invalidateQueries({ queryKey: ["/api/sites"] });
      const created = resp?.data;
      if (created?.id) setSelectedSiteId(created.id);
      toast({ title: t("toasts.siteCreated") });
    },
    onError: (e: Error) =>
      toast({ title: t("toasts.error"), description: e.message, variant: "destructive" }),
  });

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);

  // Auto-select first site once sites load.
  useEffect(() => {
    if (!selectedSiteId && sites.length > 0) setSelectedSiteId(sites[0].id);
  }, [sites, selectedSiteId]);

  return (
    <div className="flex h-screen bg-[#030407] text-white overflow-hidden">
      {!focused && <Sidebar />}
      <main
        className={`flex-1 min-w-0 overflow-y-auto ${focused ? "p-2" : "p-4 md:p-6 space-y-4"}`}
      >
        {!focused && (
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <Globe className="w-7 h-7" />
                {t("page.title")}
              </h1>
              <p className="text-white/60 mt-1 text-sm">{t("page.subtitle")}</p>
            </div>

            <div className="flex items-end gap-2">
              <div className="flex-1 min-w-[220px]">
                <Input
                  value={newSiteName}
                  onChange={(e) => setNewSiteName(e.target.value)}
                  placeholder={t("page.newSitePlaceholder")}
                  className="bg-black/30 border-white/10 text-white"
                  data-testid="input-new-site-name"
                />
              </div>
              <Button
                onClick={() => newSiteName.trim() && createSite.mutate(newSiteName.trim())}
                disabled={!newSiteName.trim() || createSite.isPending}
                data-testid="button-create-site"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {t("page.create")}
              </Button>
            </div>
          </header>
        )}

        {!focused && sites.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {sites.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedSiteId(s.id)}
                className={`px-3 py-1.5 rounded-lg border text-sm transition flex items-center gap-2 ${
                  selectedSiteId === s.id
                    ? "border-blue-500 bg-blue-500/15 text-white"
                    : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
                }`}
                data-testid={`button-select-site-${s.id}`}
              >
                <Globe className="w-3.5 h-3.5" />
                {s.name}
                <Badge
                  variant={STATUS_VARIANTS[s.status] ?? "outline"}
                  className="ml-1 text-[10px] py-0 px-1.5 h-4"
                >
                  {s.status}
                </Badge>
              </button>
            ))}
          </div>
        )}

        {!focused && sitesQuery.isLoading && (
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-36 rounded-lg" />
            ))}
          </div>
        )}
        {!focused && !sitesQuery.isLoading && sites.length === 0 && (
          <Card className="p-10 bg-white/5 border-white/10 text-center text-white/60">
            {t("page.emptyBeforeButton")} <em>{t("page.create")}</em>.
          </Card>
        )}

        {selectedSiteId && (
          <SiteBuilder
            key={selectedSiteId}
            siteId={selectedSiteId}
            onDeleted={() => setSelectedSiteId(null)}
            focused={focused}
            onToggleFocused={() => setFocused((f) => !f)}
          />
        )}
      </main>
    </div>
  );
}

// ── Builder (chat + preview + sidepanel) ────────────────────────────────

function SiteBuilder({
  siteId,
  onDeleted,
  focused,
  onToggleFocused,
}: {
  siteId: string;
  onDeleted: () => void;
  focused: boolean;
  onToggleFocused: () => void;
}): JSX.Element {
  const { t } = useTranslation("sites");
  const { toast } = useToast();
  const qc = useQueryClient();

  // ── Server data ──
  const detailQuery = useQuery<SiteDetail>({
    queryKey: ["/api/sites", siteId],
    queryFn: () => apiRequest("GET", `/api/sites/${siteId}`).then((r) => r.data ?? r),
  });

  const currentHtmlQuery = useQuery<CurrentHtmlResponse>({
    queryKey: ["/api/sites", siteId, "current-html"],
    queryFn: () => apiRequest("GET", `/api/sites/${siteId}/current-html`).then((r) => r.data ?? r),
  });

  const assetsQuery = useQuery<SiteAsset[]>({
    queryKey: ["/api/sites", siteId, "assets"],
    queryFn: () => apiRequest("GET", `/api/sites/${siteId}/assets`).then((r) => r.data ?? r),
  });

  const modelsQuery = useQuery<BuilderModel[]>({
    queryKey: ["/api/sites/builder/models"],
    queryFn: () => apiRequest("GET", "/api/sites/builder/models").then((r) => r.data ?? r),
  });
  const models = modelsQuery.data ?? FALLBACK_MODELS;

  // ── Local state ──
  const [chat, setChat] = useState<ChatTurn[]>([]);
  const [chatInput, setChatInput] = useState("");
  // Images pasted/uploaded with the next chat turn. Each entry is the
  // publicUrl returned by the asset endpoint; we upload immediately on
  // paste so the user sees a real thumbnail and the URL is ready to embed.
  const [pendingImages, setPendingImages] = useState<
    { id: string; url: string; fileName: string }[]
  >([]);
  const [uploadingChatImage, setUploadingChatImage] = useState(false);
  const [modelKey, setModelKey] = useState<string>("sonnet-4-6");
  const [autoPublish, setAutoPublish] = useState(false);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  // The HTML currently shown in the preview pane. Local edits (manual code or AI edits)
  // override the server "current published" HTML until the user reloads.
  const [workingHtml, setWorkingHtml] = useState<string>("");
  const [hasLocalEdits, setHasLocalEdits] = useState(false);

  // Initial load of current HTML into working buffer.
  useEffect(() => {
    if (currentHtmlQuery.data && !hasLocalEdits) {
      setWorkingHtml(currentHtmlQuery.data.html ?? "");
    }
  }, [currentHtmlQuery.data, hasLocalEdits]);

  // ── Mutations ──
  const chatEdit = useMutation({
    mutationFn: (payload: { message: string; images: string[] }) =>
      apiRequest(
        "POST",
        `/api/sites/${siteId}/chat`,
        {
          message: payload.message,
          modelKey,
          history: chat.map((t) => ({ role: t.role, content: t.content })),
          currentHtml: workingHtml,
          autoSave: true,
          publish: autoPublish,
          images: payload.images,
        },
        { timeoutMs: 360_000 }
      ),
    onSuccess: (resp: { data?: ChatResponse }) => {
      const data = resp?.data;
      if (!data) return;
      setChat((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.assistantText,
          savedVersion: data.savedVersion ?? undefined,
        },
      ]);
      if (data.updatedHtml) {
        setWorkingHtml(data.updatedHtml);
        setHasLocalEdits(true);
      }
      qc.invalidateQueries({ queryKey: ["/api/sites", siteId] });
      qc.invalidateQueries({ queryKey: ["/api/sites", siteId, "current-html"] });
      if (data.tokensCharged > 0) {
        toast({
          title: t("toasts.changeGenerated"),
          description: t("toasts.tokensUsed", {
            charged: data.tokensCharged,
            remaining: data.tokensRemaining,
          }),
        });
      }
    },
    onError: (e: Error) => {
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: t("chat.errorMessage", { message: e.message }) },
      ]);
      toast({ title: t("toasts.aiNoResponse"), description: e.message, variant: "destructive" });
    },
  });

  const saveDraft = useMutation({
    mutationFn: (publish: boolean) =>
      apiRequest("POST", `/api/sites/${siteId}/versions/html`, {
        html: workingHtml,
        isSpa: false,
      }).then(async (resp) => {
        // versions/html auto-publishes; if user only wanted a draft, no separate path yet — keep behavior.
        return resp;
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/sites", siteId] });
      qc.invalidateQueries({ queryKey: ["/api/sites", siteId, "current-html"] });
      setHasLocalEdits(false);
      toast({ title: t("toasts.versionSavedPublished") });
    },
    onError: (e: Error) =>
      toast({ title: t("toasts.saveFailed"), description: e.message, variant: "destructive" }),
  });

  const publishVersion = useMutation({
    mutationFn: (versionId: string) =>
      apiRequest("POST", `/api/sites/${siteId}/rollback/${versionId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/sites", siteId] });
      qc.invalidateQueries({ queryKey: ["/api/sites", siteId, "current-html"] });
      toast({ title: t("toasts.versionPublished") });
    },
    onError: (e: Error) =>
      toast({ title: t("toasts.publishFailed"), description: e.message, variant: "destructive" }),
  });

  const deleteSite = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/sites/${siteId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/sites"] });
      onDeleted();
      toast({ title: t("toasts.siteDeleted") });
    },
  });

  // ── Render ──

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-3">
        {/* Site title bar skeleton */}
        <div className="flex items-center justify-between gap-3 px-1">
          <Skeleton className="h-7 w-48" />
          <div className="flex items-center gap-1">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-8 w-28" />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-3">
          {!focused && (
            <div className="col-span-12 lg:col-span-3 min-h-[600px] lg:min-h-[calc(100vh-220px)]">
              <Card className="h-full bg-white/5 border-white/10 flex flex-col p-3 gap-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="flex-1 w-full" />
                <Skeleton className="h-[90px] w-full" />
              </Card>
            </div>
          )}
          <div
            className={`col-span-12 ${focused ? "lg:col-span-12 lg:min-h-[calc(100vh-100px)]" : "lg:col-span-6 lg:min-h-[calc(100vh-220px)]"} min-h-[600px]`}
          >
            <Card className="h-full bg-white/5 border-white/10 flex flex-col p-3 gap-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="flex-1 w-full" />
            </Card>
          </div>
          {!focused && (
            <div className="col-span-12 lg:col-span-3 min-h-[600px] lg:min-h-[calc(100vh-220px)]">
              <Card className="h-full bg-white/5 border-white/10 flex flex-col p-3 gap-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="flex-1 w-full" />
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (!detailQuery.data) {
    return (
      <Card className="p-6 bg-white/5 border-white/10 text-white/50">{t("builder.noData")}</Card>
    );
  }

  const { site, versions, domains } = detailQuery.data;
  const assets = assetsQuery.data ?? [];

  return (
    <div className="space-y-3">
      {/* Site title bar */}
      <div className="flex items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="text-xl font-bold truncate">{site.name}</h2>
          {hasLocalEdits && (
            <Badge
              variant="secondary"
              className="bg-yellow-500/20 text-yellow-200 border-yellow-500/30"
            >
              {t("builder.unsavedChanges")}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-white/70 hover:bg-white/10 hover:text-white"
            onClick={onToggleFocused}
            title={focused ? t("builder.exitFocus") : t("builder.enterFocus")}
            data-testid="button-toggle-focus"
          >
            {focused ? (
              <Minimize2 className="w-4 h-4 mr-1.5" />
            ) : (
              <Maximize2 className="w-4 h-4 mr-1.5" />
            )}
            {focused ? t("builder.exitFocus") : t("builder.enterFocus")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-300 hover:bg-red-500/10 hover:text-red-200"
            onClick={() => {
              if (confirm(t("builder.deleteConfirm", { name: site.name }))) {
                deleteSite.mutate();
              }
            }}
            data-testid="button-delete-site"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            {t("builder.deleteSite")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        {/* CHAT */}
        <div
          className={`${focused ? "hidden" : "col-span-12 lg:col-span-3"} min-h-[600px] lg:min-h-[calc(100vh-220px)]`}
        >
          <ChatPanel
            chat={chat}
            input={chatInput}
            onInputChange={setChatInput}
            onSend={() => {
              const m = chatInput.trim();
              if (!m && pendingImages.length === 0) return;
              const urls = pendingImages.map((p) => p.url);
              setChat((prev) => [...prev, { role: "user", content: m, attachedImages: urls }]);
              setChatInput("");
              setPendingImages([]);
              chatEdit.mutate({ message: m, images: urls });
            }}
            isLoading={chatEdit.isPending}
            modelKey={modelKey}
            onModelChange={setModelKey}
            models={models}
            autoPublish={autoPublish}
            onAutoPublishChange={setAutoPublish}
            assetCount={assets.length}
            siteId={siteId}
            pendingImages={pendingImages}
            uploadingImage={uploadingChatImage}
            onAddImage={async (file) => {
              setUploadingChatImage(true);
              try {
                const fd = new FormData();
                fd.append("file", file);
                const resp = await uploadFile(`/api/sites/${siteId}/assets`, fd);
                const a = (resp as { data?: SiteAsset }).data ?? (resp as SiteAsset);
                if (a?.id && a?.publicUrl) {
                  setPendingImages((prev) => [
                    ...prev,
                    { id: a.id, url: a.publicUrl, fileName: a.fileName },
                  ]);
                  qc.invalidateQueries({ queryKey: ["/api/sites", siteId, "assets"] });
                }
              } catch (err) {
                toast({
                  title: t("toasts.uploadFailed"),
                  description: (err as Error).message,
                  variant: "destructive",
                });
              } finally {
                setUploadingChatImage(false);
              }
            }}
            onRemoveImage={(id) => setPendingImages((prev) => prev.filter((p) => p.id !== id))}
          />
        </div>

        {/* PREVIEW */}
        <div
          className={`col-span-12 ${focused ? "lg:col-span-12 lg:min-h-[calc(100vh-100px)]" : "lg:col-span-6 lg:min-h-[calc(100vh-220px)]"} min-h-[600px]`}
        >
          <PreviewPanel
            html={workingHtml}
            mode={previewMode}
            onModeChange={setPreviewMode}
            onHtmlChange={(next) => {
              setWorkingHtml(next);
              setHasLocalEdits(true);
            }}
            onSavePublish={() => saveDraft.mutate(true)}
            saving={saveDraft.isPending}
            hasLocalEdits={hasLocalEdits}
            onReload={() => {
              setHasLocalEdits(false);
              currentHtmlQuery.refetch();
            }}
            currentVersionNumber={currentHtmlQuery.data?.versionNumber ?? null}
          />
        </div>

        {/* SIDEPANEL */}
        <div
          className={`${focused ? "hidden" : "col-span-12 lg:col-span-3"} min-h-[600px] lg:min-h-[calc(100vh-220px)]`}
        >
          <SidePanel
            siteId={siteId}
            site={site}
            assets={assets}
            assetsLoading={assetsQuery.isLoading}
            versions={versions}
            domains={domains}
            onPublishVersion={(vId) => publishVersion.mutate(vId)}
            publishingVersionId={
              publishVersion.isPending ? (publishVersion.variables as string) : null
            }
          />
        </div>
      </div>
    </div>
  );
}

// ── Chat Panel ──────────────────────────────────────────────────────────

function ChatPanel(props: {
  chat: ChatTurn[];
  input: string;
  onInputChange: (v: string) => void;
  onSend: () => void;
  isLoading: boolean;
  modelKey: string;
  onModelChange: (k: string) => void;
  models: BuilderModel[];
  autoPublish: boolean;
  onAutoPublishChange: (v: boolean) => void;
  assetCount: number;
  siteId: string;
  pendingImages: { id: string; url: string; fileName: string }[];
  uploadingImage: boolean;
  onAddImage: (file: File) => Promise<void> | void;
  onRemoveImage: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation("sites");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [props.chat.length, props.isLoading, props.pendingImages.length]);

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files) return;
    const list = Array.from(files);
    for (const f of list) {
      if (!f.type.startsWith("image/")) continue;
      // Soft limit of 4 — backend enforces the hard cap.
      if (props.pendingImages.length >= 4) break;
      await props.onAddImage(f);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const files: File[] = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f && f.type.startsWith("image/")) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      await handleFiles(files);
    }
  };

  const activeModel = useMemo(
    () => props.models.find((m) => m.key === props.modelKey) ?? props.models[0],
    [props.models, props.modelKey]
  );

  return (
    <Card className="h-full bg-white/5 border-white/10 flex flex-col p-3">
      <div className="flex items-center justify-between gap-2 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2 font-semibold">
          <Sparkles className="w-4 h-4 text-blue-400" />
          {t("chat.title")}
        </div>
        <Select value={props.modelKey} onValueChange={props.onModelChange}>
          <SelectTrigger className="w-[220px] h-8 text-xs bg-black/30 border-white/10">
            <SelectValue placeholder={t("chat.modelPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {props.models.map((m) => (
              <SelectItem key={m.key} value={m.key}>
                <div className="flex flex-col">
                  <span>{m.label}</span>
                  <span className="text-[10px] text-white/50">
                    {t("chat.modelTokenMultiplier", { multiplier: m.multiplier })}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {activeModel?.description && (
        <p className="pt-2 text-[11px] text-white/40 leading-snug">{activeModel.description}</p>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 space-y-3 min-h-[200px]">
        {props.chat.length === 0 && (
          <div className="text-white/40 text-xs space-y-2 leading-relaxed px-1">
            <p>
              <strong className="text-white/70">{t("chat.emptyTitle")}</strong>{" "}
              {t("chat.emptyDescription")}
            </p>
            <p>
              {props.assetCount > 0
                ? t("chat.assetsAvailable", { count: props.assetCount })
                : t("chat.uploadAssetsHint")}
            </p>
          </div>
        )}

        {props.chat.map((t, i) => (
          <ChatBubble key={i} turn={t} />
        ))}

        {props.isLoading && (
          <div className="flex items-center gap-2 text-white/50 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("chat.aiWorking")}
          </div>
        )}
      </div>

      <div className="border-t border-white/10 pt-3 space-y-2">
        <label className="flex items-center gap-2 text-xs text-white/60">
          <input
            type="checkbox"
            checked={props.autoPublish}
            onChange={(e) => props.onAutoPublishChange(e.target.checked)}
          />
          {t("chat.autoPublish")}
        </label>
        {(props.pendingImages.length > 0 || props.uploadingImage) && (
          <div className="flex flex-wrap gap-2 p-2 rounded-md border border-white/10 bg-black/20">
            {props.pendingImages.map((img) => (
              <div
                key={img.id}
                className="relative group h-14 w-14 rounded overflow-hidden border border-white/10"
                data-testid={`chat-image-thumb-${img.id}`}
              >
                <img src={img.url} alt={img.fileName} className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => props.onRemoveImage(img.id)}
                  className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/70 text-white/90 hover:bg-red-500 flex items-center justify-center text-[10px]"
                  title={t("chat.removeImage")}
                  data-testid={`button-remove-chat-image-${img.id}`}
                >
                  ×
                </button>
              </div>
            ))}
            {props.uploadingImage && (
              <div className="h-14 w-14 rounded border border-dashed border-white/20 flex items-center justify-center bg-black/30">
                <Loader2 className="w-4 h-4 animate-spin text-white/50" />
              </div>
            )}
          </div>
        )}
        <div
          className="relative"
          onDragOver={(e) => {
            if (Array.from(e.dataTransfer?.items ?? []).some((it) => it.kind === "file")) {
              e.preventDefault();
            }
          }}
          onDrop={(e) => {
            const files = Array.from(e.dataTransfer?.files ?? []).filter((f) =>
              f.type.startsWith("image/")
            );
            if (files.length > 0) {
              e.preventDefault();
              void handleFiles(files);
            }
          }}
        >
          <Textarea
            value={props.input}
            onChange={(e) => props.onInputChange(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                props.onSend();
              }
            }}
            placeholder={t("chat.inputPlaceholder")}
            className="bg-black/30 border-white/10 text-sm min-h-[90px] resize-none pr-20"
            enableDictation={false}
            data-testid="textarea-chat-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              void handleFiles(e.target.files);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            data-testid="input-chat-image-file"
          />
          <Button
            size="sm"
            variant="ghost"
            className="absolute bottom-2 right-11 h-8 w-8 p-0 text-white/70 hover:bg-white/10"
            onClick={() => fileInputRef.current?.click()}
            disabled={props.uploadingImage || props.pendingImages.length >= 4}
            title={t("chat.attachImage")}
            data-testid="button-chat-attach-image"
          >
            <ImagePlus className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            className="absolute bottom-2 right-2 h-8 w-8 p-0"
            onClick={props.onSend}
            disabled={props.isLoading || (!props.input.trim() && props.pendingImages.length === 0)}
            data-testid="button-chat-send"
          >
            {props.isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        <p className="text-[10px] text-white/40">
          {t("chat.sendShortcut")} · {t("chat.pasteHint")}
        </p>
      </div>
    </Card>
  );
}

function ChatBubble({ turn }: { turn: ChatTurn }): JSX.Element {
  const { t } = useTranslation("sites");
  const isUser = turn.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[92%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${
          isUser
            ? "bg-blue-500/20 border border-blue-500/30 text-white"
            : "bg-white/5 border border-white/10 text-white/90"
        }`}
      >
        {turn.attachedImages && turn.attachedImages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {turn.attachedImages.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                <img
                  src={url}
                  alt=""
                  className="h-16 w-16 object-cover rounded border border-white/10"
                />
              </a>
            ))}
          </div>
        )}
        {turn.content}
        {turn.savedVersion && (
          <div className="mt-2 pt-2 border-t border-white/10 text-[11px] text-white/60 flex items-center gap-1.5">
            <Save className="w-3 h-3" />
            {t("chat.versionSaved", { version: turn.savedVersion.versionNumber })}
            {turn.savedVersion.isPublished && (
              <Badge variant="default" className="ml-1 text-[10px] py-0 px-1.5 h-4">
                {t("common.live")}
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Preview Panel ───────────────────────────────────────────────────────

function PreviewPanel(props: {
  html: string;
  mode: "desktop" | "mobile";
  onModeChange: (m: "desktop" | "mobile") => void;
  onHtmlChange: (v: string) => void;
  onSavePublish: () => void;
  saving: boolean;
  hasLocalEdits: boolean;
  onReload: () => void;
  currentVersionNumber: number | null;
}): JSX.Element {
  const { t } = useTranslation("sites");
  const [tab, setTab] = useState<"preview" | "code">("preview");

  return (
    <Card className="h-full bg-white/5 border-white/10 flex flex-col p-3">
      <div className="flex items-center justify-between gap-2 pb-3 border-b border-white/10 flex-wrap">
        <Tabs value={tab} onValueChange={(v) => setTab(v as "preview" | "code")}>
          <TabsList className="bg-black/30 h-8">
            <TabsTrigger value="preview" className="h-6 text-xs">
              {t("preview.tabPreview")}
            </TabsTrigger>
            <TabsTrigger value="code" className="h-6 text-xs">
              <Code2 className="w-3 h-3 mr-1" /> {t("preview.tabCode")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
          {tab === "preview" && (
            <ToggleGroup
              type="single"
              value={props.mode}
              onValueChange={(v) => v && props.onModeChange(v as "desktop" | "mobile")}
              className="bg-black/30 rounded-md"
            >
              <ToggleGroupItem value="desktop" className="h-8 px-2.5">
                <Monitor className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="mobile" className="h-8 px-2.5">
                <Smartphone className="w-4 h-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          )}

          {tab === "preview" && (
            <div className="hidden sm:block w-px h-6 self-center bg-white/10 shrink-0" />
          )}

          {/* Secondary actions — stay grouped together when the bar wraps */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-black/30 border-white/10"
              onClick={() => {
                const blob = new Blob([props.html], { type: "text/html" });
                const url = URL.createObjectURL(blob);
                const win = window.open(url, "_blank", "noopener,noreferrer");
                if (!win) URL.revokeObjectURL(url);
                else setTimeout(() => URL.revokeObjectURL(url), 60_000);
              }}
              disabled={!props.html.trim()}
              title={t("preview.openNewTabTitle")}
              data-testid="button-open-new-tab"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> {t("preview.openNewTab")}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-black/30 border-white/10"
              onClick={props.onReload}
              disabled={!props.hasLocalEdits}
              title={t("preview.resetTitle")}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> {t("preview.reset")}
            </Button>
          </div>

          <div className="hidden sm:block w-px h-6 self-center bg-white/10 shrink-0" />

          <Button
            size="sm"
            className="h-8 bg-orange-500 hover:bg-orange-600"
            onClick={props.onSavePublish}
            disabled={props.saving || !props.html.trim()}
            data-testid="button-save-publish"
          >
            {props.saving ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-1" />
            )}
            {t("preview.savePublish")}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-3">
        {tab === "preview" ? (
          <PreviewFrame
            html={props.html}
            mode={props.mode}
            versionNumber={props.currentVersionNumber}
          />
        ) : (
          <Textarea
            value={props.html}
            onChange={(e) => props.onHtmlChange(e.target.value)}
            placeholder="<!DOCTYPE html>\n<html>\n  …\n</html>"
            className="h-full min-h-[400px] bg-black/40 border-white/10 font-mono text-xs resize-none"
            data-testid="textarea-html-code"
          />
        )}
      </div>
    </Card>
  );
}

function PreviewFrame(props: {
  html: string;
  mode: "desktop" | "mobile";
  versionNumber: number | null;
}): JSX.Element {
  const { t } = useTranslation("sites");
  // Debounce so typing in the code tab doesn't thrash the iframe.
  const [debounced, setDebounced] = useState(props.html);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(props.html), 220);
    return () => clearTimeout(handle);
  }, [props.html]);

  if (!props.html.trim()) {
    return (
      <div className="w-full h-full min-h-[400px] rounded border border-dashed border-white/10 bg-black/20 flex items-center justify-center text-sm text-white/40 px-6 text-center">
        {t("preview.empty")}
      </div>
    );
  }

  if (props.mode === "mobile") {
    return (
      <div className="w-full h-full overflow-auto flex items-start justify-center bg-black/30 rounded p-4">
        <div className="bg-black rounded-[28px] p-2 shadow-2xl border border-white/10">
          <iframe
            title={t("preview.mobileFrameTitle")}
            sandbox="allow-scripts allow-same-origin"
            srcDoc={debounced}
            className="w-[390px] h-[780px] rounded-[20px] bg-white"
          />
        </div>
      </div>
    );
  }

  return (
    <iframe
      title={t("preview.desktopFrameTitle")}
      sandbox="allow-scripts allow-same-origin"
      srcDoc={debounced}
      className="w-full h-full min-h-[500px] rounded border border-white/10 bg-white"
    />
  );
}

// ── Side Panel (assets, versions, domains) ──────────────────────────────

function SidePanel(props: {
  siteId: string;
  site: HostedSite;
  assets: SiteAsset[];
  assetsLoading: boolean;
  versions: HostedVersion[];
  domains: HostedDomain[];
  onPublishVersion: (vId: string) => void;
  publishingVersionId: string | null;
}): JSX.Element {
  const { t } = useTranslation("sites");
  return (
    <Card className="h-full bg-white/5 border-white/10 flex flex-col p-3">
      <Tabs defaultValue="assets" className="h-full flex flex-col">
        <TabsList className="bg-black/30 grid grid-cols-3 h-8">
          <TabsTrigger value="assets" className="text-xs px-2 py-0">
            <ImagePlus className="w-3 h-3 mr-1" /> {t("sidePanel.media")}
          </TabsTrigger>
          <TabsTrigger value="versions" className="text-xs px-2 py-0">
            v{props.versions.length}
          </TabsTrigger>
          <TabsTrigger value="domains" className="text-xs px-2 py-0">
            <Globe className="w-3 h-3 mr-1" /> {t("sidePanel.domains")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="flex-1 min-h-0 mt-3">
          <AssetsTab siteId={props.siteId} assets={props.assets} loading={props.assetsLoading} />
        </TabsContent>

        <TabsContent value="versions" className="flex-1 min-h-0 mt-3">
          <VersionsTab
            site={props.site}
            versions={props.versions}
            onPublish={props.onPublishVersion}
            publishingId={props.publishingVersionId}
          />
        </TabsContent>

        <TabsContent value="domains" className="flex-1 min-h-0 mt-3">
          <DomainsTab siteId={props.siteId} domains={props.domains} />
        </TabsContent>
      </Tabs>
    </Card>
  );
}

// ── Assets Tab ──────────────────────────────────────────────────────────

function AssetsTab(props: { siteId: string; assets: SiteAsset[]; loading: boolean }): JSX.Element {
  const { t } = useTranslation("sites");
  const { toast } = useToast();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return uploadFile(`/api/sites/${props.siteId}/assets`, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/sites", props.siteId, "assets"] });
      toast({ title: t("toasts.fileUploaded") });
    },
    onError: (e: Error) =>
      toast({ title: t("toasts.uploadFailed"), description: e.message, variant: "destructive" }),
    onSettled: () => setUploading(false),
  });

  const remove = useMutation({
    mutationFn: (assetId: string) =>
      apiRequest("DELETE", `/api/sites/${props.siteId}/assets/${assetId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/sites", props.siteId, "assets"] });
      toast({ title: t("toasts.fileDeleted") });
    },
  });

  const onPick = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    upload.mutate(files[0]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const copyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    toast({ title: t("toasts.urlCopied") });
  };

  return (
    <ScrollArea className="h-full">
      <div className="space-y-3 pr-2">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onPick(e.dataTransfer.files);
          }}
          className="rounded-lg border border-dashed border-white/15 bg-black/30 p-4 text-center"
        >
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*,audio/*,.woff,.woff2,.ttf,.otf,.eot"
            onChange={(e) => onPick(e.target.files)}
          />
          <Button
            size="sm"
            variant="outline"
            className="bg-black/30 border-white/10"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="button-upload-asset"
          >
            {uploading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {t("assets.uploading")}
              </>
            ) : (
              <>
                <Upload className="w-3.5 h-3.5 mr-1" /> {t("assets.uploadFile")}
              </>
            )}
          </Button>
          <p className="text-[11px] text-white/40 mt-2">{t("assets.dropHint")}</p>
        </div>

        {props.loading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-2 p-2 rounded border border-white/10 bg-black/20"
              >
                <Skeleton className="w-12 h-12 rounded flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-2.5 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!props.loading && props.assets.length === 0 && (
          <p className="text-xs text-white/40 text-center py-4">{t("assets.empty")}</p>
        )}

        <div className="space-y-2">
          {props.assets.map((a) => (
            <AssetRow
              key={a.id}
              asset={a}
              onCopy={() => copyUrl(a.publicUrl)}
              onDelete={() => {
                if (confirm(t("assets.deleteConfirm", { fileName: a.fileName })))
                  remove.mutate(a.id);
              }}
            />
          ))}
        </div>
      </div>
    </ScrollArea>
  );
}

function AssetRow({
  asset,
  onCopy,
  onDelete,
}: {
  asset: SiteAsset;
  onCopy: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation("sites");
  const isImage = asset.mimeType.startsWith("image/");
  const isVideo = asset.mimeType.startsWith("video/");
  return (
    <div className="flex items-center gap-2 p-2 rounded border border-white/10 bg-black/20">
      <div className="w-12 h-12 flex-shrink-0 rounded bg-black/40 overflow-hidden flex items-center justify-center">
        {isImage ? (
          <img
            src={asset.publicUrl}
            alt={asset.fileName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : isVideo ? (
          <video src={asset.publicUrl} muted className="w-full h-full object-cover" />
        ) : (
          <span className="text-[10px] text-white/50 px-1 text-center">
            {asset.mimeType.split("/")[1] ?? t("assets.fileFallback")}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white/90 truncate" title={asset.fileName}>
          {asset.fileName}
        </div>
        <div className="text-[10px] text-white/40">{formatBytes(asset.sizeBytes)}</div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0"
        onClick={onCopy}
        title={t("assets.copyUrl")}
      >
        <Copy className="w-3.5 h-3.5" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0 text-red-300 hover:bg-red-500/10"
        onClick={onDelete}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// ── Versions Tab ────────────────────────────────────────────────────────

function VersionsTab(props: {
  site: HostedSite;
  versions: HostedVersion[];
  onPublish: (vId: string) => void;
  publishingId: string | null;
}): JSX.Element {
  const { t, i18n } = useTranslation("sites");
  if (props.versions.length === 0) {
    return <p className="text-xs text-white/40 text-center py-4">{t("versions.empty")}</p>;
  }
  return (
    <ScrollArea className="h-full">
      <div className="space-y-2 pr-2">
        {props.versions.map((v) => {
          const isCurrent = props.site.currentVersionId === v.id;
          const isPublishing = props.publishingId === v.id;
          return (
            <div
              key={v.id}
              className="flex items-center justify-between gap-2 p-2 rounded border border-white/10 bg-black/20"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">v{v.versionNumber}</span>
                  {isCurrent && (
                    <Badge variant="default" className="text-[10px] py-0 px-1.5 h-4">
                      {t("common.live")}
                    </Badge>
                  )}
                </div>
                <div className="text-[10px] text-white/40">
                  {formatBytes(v.sizeBytes)} ·{" "}
                  {new Date(v.uploadedAt).toLocaleString(i18n.resolvedLanguage ?? i18n.language)}
                </div>
              </div>
              {!isCurrent && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 bg-black/30 border-white/10 text-xs"
                  onClick={() => props.onPublish(v.id)}
                  disabled={isPublishing}
                >
                  {isPublishing ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    t("versions.publish")
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

// ── Domains Tab ─────────────────────────────────────────────────────────

function DomainsTab(props: { siteId: string; domains: HostedDomain[] }): JSX.Element {
  const { t } = useTranslation("sites");
  const { toast } = useToast();
  const qc = useQueryClient();
  const [newDomain, setNewDomain] = useState("");

  const addDomain = useMutation({
    mutationFn: (domain: string) =>
      apiRequest("POST", `/api/sites/${props.siteId}/domains`, { domain }),
    onSuccess: () => {
      setNewDomain("");
      qc.invalidateQueries({ queryKey: ["/api/sites", props.siteId] });
      toast({ title: t("toasts.domainAdded") });
    },
    onError: (e: Error) =>
      toast({ title: t("toasts.error"), description: e.message, variant: "destructive" }),
  });

  const verify = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/sites/${props.siteId}/domains/${id}/verify`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/sites", props.siteId] }),
    onError: (e: Error) =>
      toast({
        title: t("toasts.verificationFailed"),
        description: e.message,
        variant: "destructive",
      }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/sites/${props.siteId}/domains/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/sites", props.siteId] }),
  });

  return (
    <ScrollArea className="h-full">
      <div className="space-y-3 pr-2">
        <div className="flex gap-2">
          <Input
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder={t("domains.placeholder")}
            className="bg-black/30 border-white/10 h-8 text-sm"
          />
          <Button
            size="sm"
            className="h-8"
            onClick={() => newDomain.trim() && addDomain.mutate(newDomain.trim().toLowerCase())}
            disabled={!newDomain.trim() || addDomain.isPending}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>

        {props.domains.length === 0 ? (
          <p className="text-xs text-white/40 text-center py-4">{t("domains.empty")}</p>
        ) : (
          props.domains.map((d) => (
            <DomainRow
              key={d.id}
              domain={d}
              onVerify={() => verify.mutate(d.id)}
              onRemove={() => {
                if (confirm(t("domains.deleteConfirm", { domain: d.domain }))) remove.mutate(d.id);
              }}
              verifying={verify.isPending}
            />
          ))
        )}
      </div>
    </ScrollArea>
  );
}

function DomainRow({
  domain,
  onVerify,
  onRemove,
  verifying,
}: {
  domain: HostedDomain;
  onVerify: () => void;
  onRemove: () => void;
  verifying: boolean;
}): JSX.Element {
  const { t } = useTranslation("sites");
  const isOk = domain.status === "verified" || domain.status === "active";
  return (
    <div className="p-2.5 rounded border border-white/10 bg-black/20 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {isOk ? (
            <Check className="w-3.5 h-3.5 text-green-400" />
          ) : (
            <AlertCircle className="w-3.5 h-3.5 text-yellow-400" />
          )}
          <span className="font-mono text-xs truncate">{domain.domain}</span>
        </div>
        <Badge
          variant={STATUS_VARIANTS[domain.status] ?? "outline"}
          className="text-[10px] h-4 py-0 px-1.5"
        >
          {domain.status}
        </Badge>
      </div>
      {!isOk && (
        <div className="text-[10px] bg-black/30 p-2 rounded space-y-1">
          <div className="text-white/60">
            TXT{" "}
            <span className="font-mono text-green-300 break-all">
              _viralhost-verify.{domain.domain}
            </span>{" "}
            → <span className="font-mono text-green-300 break-all">{domain.verificationToken}</span>
          </div>
          <div className="text-white/60">
            CNAME <span className="font-mono text-green-300 break-all">{domain.domain}</span> →{" "}
            <span className="font-mono text-green-300">edge.viral.partners</span>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-1">
        {!isOk && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-[11px] bg-black/30 border-white/10"
            onClick={onVerify}
            disabled={verifying}
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${verifying ? "animate-spin" : ""}`} />{" "}
            {t("domains.check")}
          </Button>
        )}
        {isOk && (
          <a
            href={`https://${domain.domain}`}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-blue-300 hover:text-blue-200 flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> {t("domains.open")}
          </a>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 text-red-300 hover:bg-red-500/10"
          onClick={onRemove}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      {domain.lastError && (
        <div className="text-[10px] text-red-300 bg-red-950/30 p-1.5 rounded">
          {domain.lastError}
        </div>
      )}
    </div>
  );
}
